@if (loadError(); as err) {
  <div class="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-800">
    {{ err }}
  </div>
}

@if (importFeedback(); as msg) {
  <div class="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12px] text-emerald-800">
    {{ msg }}
  </div>
}

<app-admin-crm-lead-onboarding-modal
  [open]="showOnboarding()"
  (cancelled)="closeOnboarding()"
  (manual)="onManualLead()"
  (imported)="onMapsImported($event)" />

@if (deleteTarget(); as target) {
  <app-admin-crm-delete-lead-modal
    [open]="true"
    [leadLabel]="leadDeleteLabel(target)"
    [deleting]="deleting()"
    (cancelled)="closeDeleteConfirm()"
    (confirmed)="confirmDeleteLead()" />
}

<!-- KPIs -->
<section class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3" aria-label="Indicadores CRM">
  <article class="rounded-2xl border border-pale-gray bg-white px-5 py-4">
    <p class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray">Total Leads</p>
    <p class="mt-1 text-[28px] font-bold text-midnight-indigo">{{ kpis().total }}</p>
  </article>
  <article class="rounded-2xl border border-pale-gray bg-white px-5 py-4">
    <p class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray">Interesados</p>
    <p class="mt-1 text-[28px] font-bold text-emerald-600">{{ kpis().interested }}</p>
  </article>
  <article class="rounded-2xl border border-pale-gray bg-white px-5 py-4">
    <p class="text-[11px] font-semibold uppercase tracking-wide text-steel-gray">Bloqueados</p>
    <p class="mt-1 text-[28px] font-bold text-red-600">{{ kpis().blocked }}</p>
  </article>
</section>

<!-- Toolbar -->
<section class="mb-4 flex flex-col gap-3 rounded-2xl border border-pale-gray bg-white p-4 lg:flex-row lg:items-center">
  <div class="relative min-w-0 flex-1">
    <lucide-icon name="search" class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-steel-gray" />
    <input
      type="search"
      [ngModel]="search()"
      (ngModelChange)="search.set($event)"
      placeholder="Buscar por local o contacto..."
      class="w-full rounded-xl border border-pale-gray py-2.5 pl-10 pr-3 text-[13px] text-midnight-indigo" />
  </div>
  <select
    [ngModel]="statusFilter()"
    (ngModelChange)="statusFilter.set($event)"
    class="rounded-xl border border-pale-gray px-3 py-2.5 text-[13px] text-midnight-indigo lg:w-48">
    <option value="all">Todos los estados</option>
    @for (s of CRM_LEAD_STATUSES; track s) {
      <option [value]="s">{{ CRM_STATUS_LABELS[s] }}</option>
    }
  </select>
  <label class="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap text-[13px] font-medium text-midnight-indigo">
    <input
      type="checkbox"
      [ngModel]="tasksTodayOnly()"
      (ngModelChange)="tasksTodayOnly.set($event)"
      class="h-4 w-4 rounded border-pale-gray text-action-blue" />
    <lucide-icon name="calendar" class="h-4 w-4 text-steel-gray" />
    Tareas para Hoy
  </label>
  <button
    type="button"
    (click)="createLead()"
    class="inline-flex items-center justify-center gap-2 rounded-xl bg-action-blue px-4 py-2.5 text-[13px] font-semibold text-white">
    <lucide-icon name="plus" [size]="16" />
    Nuevo lead
  </button>
</section>

<!-- Table -->
<section class="overflow-hidden rounded-2xl border border-pale-gray bg-white">
  @if (loading()) {
    <p class="p-6 text-[13px] text-steel-gray">Cargando leads&hellip;</p>
  } @else if (filteredLeads().length === 0) {
    <p class="p-6 text-[13px] text-steel-gray">No hay leads con estos filtros.</p>
  } @else {
    <div class="overflow-x-auto">
      <table class="w-full min-w-[760px] text-left text-[13px]">
        <thead class="border-b border-pale-gray bg-cloud-mist/60 text-[11px] font-semibold uppercase tracking-wide text-steel-gray">
          <tr>
            <th class="px-4 py-3">Local / Origen</th>
            <th class="px-4 py-3">Contacto</th>
            <th class="px-4 py-3">Estado</th>
            <th class="px-4 py-3">Exclusi&oacute;n</th>
            <th class="px-4 py-3">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-pale-gray">
          @for (lead of filteredLeads(); track lead.id) {
            <tr
              class="cursor-pointer transition hover:bg-cloud-mist/50"
              (click)="openLead(lead.id)">
              <td class="px-4 py-3">
                <div class="flex items-start gap-2">
                  @if (isTaskDue(lead)) {
                    <lucide-icon name="clock" class="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  }
                  <div class="min-w-0">
                    <p class="font-semibold text-midnight-indigo">
                      {{ lead.venueName }}
                      @if (venueCountLabel(lead); as extra) {
                        <span class="ml-1 text-[10px] font-semibold text-action-blue">({{ extra }})</span>
                      }
                    </p>
                    <p class="text-[11px] text-steel-gray">{{ sourceLabel(lead) }}</p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 text-midnight-indigo">
                {{ lead.contactName || '—' }}
              </td>
              <td class="px-4 py-3">
                <span class="inline-flex rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                      [class]="tableStatusClass(lead.status)">
                  {{ tableStatusLabel(lead.status) }}
                </span>
              </td>
              <td class="px-4 py-3" (click)="$event.stopPropagation()">
                @if (lead.status === 'lost') {
                  <span class="inline-flex items-center gap-1 text-[12px] font-semibold text-red-600">
                    <lucide-icon name="shield-off" [size]="14" />
                    Bloqueado
                  </span>
                } @else {
                  <button
                    type="button"
                    [disabled]="savingId() === lead.id"
                    (click)="blockLead(lead, $event)"
                    class="inline-flex items-center gap-1 rounded-lg border border-pale-gray px-2.5 py-1 text-[11px] font-semibold text-steel-gray hover:border-red-200 hover:text-red-700 disabled:opacity-50">
                    <lucide-icon name="shield" [size]="14" />
                    Bloquear
                  </button>
                }
              </td>
              <td class="px-4 py-3" (click)="$event.stopPropagation()">
                <div class="flex items-center gap-2">
                  <select
                    [value]="lead.status"
                    [disabled]="savingId() === lead.id"
                    (change)="onStatusChange(lead, $any($event.target).value, $event)"
                    class="rounded-lg border border-pale-gray px-2 py-1.5 text-[12px] text-midnight-indigo">
                    @for (s of CRM_LEAD_STATUSES; track s) {
                      <option [value]="s">{{ CRM_STATUS_LABELS[s] }}</option>
                    }
                  </select>
                  <button
                    type="button"
                    [disabled]="deleting()"
                    (click)="openDeleteConfirm(lead, $event)"
                    class="rounded-lg border border-pale-gray p-1.5 text-steel-gray hover:border-red-200 hover:text-red-600 disabled:opacity-50"
                    aria-label="Eliminar lead">
                    <lucide-icon name="trash-2" [size]="14" />
                  </button>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }
</section>
